import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Plus, Edit, Trash2, Search, Filter,
  CheckCircle, XCircle, RefreshCw, ChevronLeft, ChevronRight,
  Users, Key, Info, AlertTriangle, UserCheck, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdmin } from '../utils/roleRedirect';
import { GridLoader } from '../components/CommonLoader';

const Roles = () => {
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [dataroomUuid, setDataroomUuid] = useState(null); // For admin roles

  // Sample data
  const sampleRoles = [
    {
      id: 1,
      name: 'super-admin',
      display_name: 'Super Administrator',
      description: 'Full system access with all permissions',
      user_count: 2,
      permission_count: 50,
      created_at: '2024-01-15T10:00:00Z',
      is_system: true
    },
    {
      id: 2,
      name: 'admin',
      display_name: 'Administrator',
      description: 'Administrative access with most permissions',
      user_count: 5,
      permission_count: 35,
      created_at: '2024-01-15T10:00:00Z',
      is_system: true
    },
    {
      id: 3,
      name: 'manager',
      display_name: 'Manager',
      description: 'Manage users and content within assigned scope',
      user_count: 12,
      permission_count: 20,
      created_at: '2024-01-15T10:00:00Z',
      is_system: false
    },
    {
      id: 4,
      name: 'editor',
      display_name: 'Editor',
      description: 'Create and edit content',
      user_count: 25,
      permission_count: 15,
      created_at: '2024-01-20T10:00:00Z',
      is_system: false
    },
    {
      id: 5,
      name: 'viewer',
      display_name: 'Viewer',
      description: 'Read-only access to content',
      user_count: 100,
      permission_count: 5,
      created_at: '2024-01-20T10:00:00Z',
      is_system: false
    }
  ];

  const samplePermissions = [
    { id: 1, name: 'view_users', display_name: 'View Users', category: 'Users' },
    { id: 2, name: 'manage_users', display_name: 'Manage Users', category: 'Users' },
    { id: 3, name: 'delete_users', display_name: 'Delete Users', category: 'Users' },
    { id: 4, name: 'view_roles', display_name: 'View Roles', category: 'Roles' },
    { id: 5, name: 'manage_roles', display_name: 'Manage Roles', category: 'Roles' },
    { id: 6, name: 'view_permissions', display_name: 'View Permissions', category: 'Permissions' },
    { id: 7, name: 'manage_permissions', display_name: 'Manage Permissions', category: 'Permissions' },
    { id: 8, name: 'view_datarooms', display_name: 'View Datarooms', category: 'Datarooms' },
    { id: 9, name: 'manage_datarooms', display_name: 'Manage Datarooms', category: 'Datarooms' },
    { id: 10, name: 'view_subscriptions', display_name: 'View Subscriptions', category: 'Subscriptions' },
    { id: 11, name: 'manage_subscriptions', display_name: 'Manage Subscriptions', category: 'Subscriptions' },
    { id: 12, name: 'view_pricing_plans', display_name: 'View Pricing Plans', category: 'Pricing' },
    { id: 13, name: 'manage_pricing_plans', display_name: 'Manage Pricing Plans', category: 'Pricing' },
    { id: 14, name: 'view_features', display_name: 'View Features', category: 'Features' },
    { id: 15, name: 'manage_features', display_name: 'Manage Features', category: 'Features' }
  ];

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [searchTerm]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      
      let response;
      if (superAdmin) {
        // Super Admin: Fetch all dataroom roles
        response = await roleService.admin.list({ search: searchTerm });
      } else {
        // Admin: For now, use sample data as we need dataroom UUID
        // In real implementation, this would be fetched based on selected dataroom
        setTimeout(() => {
          let filteredRoles = [...sampleRoles];
          
          if (searchTerm) {
            filteredRoles = filteredRoles.filter(role => 
              role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              role.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              role.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          
          setRoles(filteredRoles);
          setLoading(false);
        }, 500);
        return;
      }
      
      // Handle different response structures
      let rolesData = [];
      if (Array.isArray(response.data)) {
        rolesData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        rolesData = response.data.data;
      } else if (response.data && response.data.roles && Array.isArray(response.data.roles)) {
        rolesData = response.data.roles;
      } else if (Array.isArray(response)) {
        rolesData = response;
      }
      setRoles(rolesData);
      setLoading(false);
    } catch (error) {
      // Fallback to sample data if API fails
      let filteredRoles = [...sampleRoles];
      
      if (searchTerm) {
        filteredRoles = filteredRoles.filter(role => 
          role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          role.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          role.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setRoles(filteredRoles);
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await permissionService.list();
      setAvailablePermissions(response.data || samplePermissions);
    } catch (error) {
      // Fallback to sample data
      setAvailablePermissions(samplePermissions);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
    } else if (!/^[a-z-]+$/.test(formData.name)) {
      errors.name = 'Role name must be lowercase with hyphens only';
    }
    
    if (!formData.display_name.trim()) {
      errors.display_name = 'Display name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      if (superAdmin) {
        // Super Admin API calls
        if (selectedRole) {
          await roleService.admin.update(selectedRole.id, formData);
          toast.success('Role updated successfully');
        } else {
          await roleService.admin.create(formData);
          toast.success('Role created successfully');
        }
      } else if (dataroomUuid) {
        // Admin API calls (requires dataroom UUID)
        if (selectedRole) {
          await roleService.dataroom.update(dataroomUuid, selectedRole.id, formData);
          toast.success('Role updated successfully');
        } else {
          await roleService.dataroom.create(dataroomUuid, formData);
          toast.success('Role created successfully');
        }
      } else {
        // Fallback for demo
        toast.success(selectedRole ? 'Role updated successfully' : 'Role created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save role');
    }
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      display_name: role.display_name,
      description: role.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      // Simulate API call
      toast.success('Role deleted successfully');
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const handleManagePermissions = (role) => {
    setSelectedRole(role);
    // Simulate getting role permissions
    const rolePermissionIds = role.name === 'super-admin' ? samplePermissions.map(p => p.id) :
                            role.name === 'admin' ? [1, 2, 4, 5, 8, 9, 10, 11, 12, 13] :
                            role.name === 'manager' ? [1, 8, 9, 10] :
                            role.name === 'editor' ? [8, 9] :
                            [8];
    setSelectedPermissions(rolePermissionIds);
    setShowPermissionsModal(true);
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSavePermissions = async () => {
    try {
      await permissionService.assignToRole(selectedRole.id, selectedPermissions);
      toast.success('Permissions updated successfully');
      setShowPermissionsModal(false);
      setSelectedRole(null);
      setSelectedPermissions([]);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update permissions');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: ''
    });
    setFormErrors({});
    setSelectedRole(null);
  };

  // Group permissions by category
  const groupedPermissions = samplePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Roles Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage user roles and their permissions
        </p>
      </div>

      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between mb-6">
          <div className="flex-1">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search roles..."
                className="w-full pl-10 pr-4 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={fetchRoles}
              className="bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors px-3 py-1.5 text-sm"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Role
            </button>
          </div>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <GridLoader cards={6} />
        ) : roles.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No roles found
          </div>
        ) : (
          Array.isArray(roles) && roles.map((role) => (
            <motion.div
              key={role.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <Shield className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{role.display_name}</h3>
                      <p className="text-sm text-gray-500">{role.name}</p>
                    </div>
                  </div>
                  {role.is_system && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                      System
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  {role.description || 'No description'}
                </p>

                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    <span>{role.user_count} users</span>
                  </div>
                  <div className="flex items-center">
                    <Key className="w-4 h-4 mr-1" />
                    <span>{role.permission_count} permissions</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleManagePermissions(role)}
                    className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm flex items-center justify-center focus:border-transparent"
                  >
                    <Key className="w-4 h-4 mr-1" />
                    Permissions
                  </button>
                  {!role.is_system && (
                    <>
                      <button
                        onClick={() => handleEdit(role)}
                        className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors focus:border-transparent text-sm"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRole(role);
                          setShowDeleteModal(true);
                        }}
                        className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors focus:border-transparent text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
          >
            <button
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold mb-4">
              {selectedRole ? 'Edit Role' : 'Create New Role'}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.name ? 'border-red-500' : ''
                  }`}
                  placeholder="e.g., content-manager"
                  disabled={selectedRole?.is_system}
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Use lowercase letters and hyphens only
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.display_name ? 'border-red-500' : ''
                  }`}
                  placeholder="e.g., Content Manager"
                />
                {formErrors.display_name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.display_name}</p>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter role description..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white rounded-md hover:bg-blue-700 px-3 py-1.5 text-sm"
                >
                  {selectedRole ? 'Update' : 'Create'} Role
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedRole && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden relative"
          >
            <div className="p-6 border-b relative">
              <button
                onClick={() => {
                  setShowPermissionsModal(false);
                  setSelectedRole(null);
                  setSelectedPermissions([]);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-lg font-semibold">
                Manage Permissions - {selectedRole.display_name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Select the permissions for this role
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {Object.entries(groupedPermissions).map(([category, permissions]) => (
                <div key={category} className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <UserCheck className="w-4 h-4 mr-2" />
                    {category}
                  </h4>
                  <div className="space-y-2">
                    {permissions.map(permission => (
                      <label
                        key={permission.id}
                        className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          disabled={selectedRole.is_system}
                        />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {permission.display_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {permission.name}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {selectedPermissions.length} permissions selected
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowPermissionsModal(false);
                      setSelectedRole(null);
                      setSelectedPermissions([]);
                    }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePermissions}
                    className="bg-blue-600 text-white rounded-md hover:bg-blue-700 px-3 py-1.5 text-sm"
                    disabled={selectedRole.is_system}
                  >
                    Save Permissions
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRole && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
          >
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedRole(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold">Delete Role</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the role "{selectedRole.display_name}"? 
              This will remove the role from all {selectedRole.user_count} users.
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex">
                <Info className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
                <p className="text-sm text-yellow-800">
                  This action cannot be undone. Users with this role will lose their associated permissions.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedRole(null);
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white rounded-md hover:bg-red-700 px-3 py-1.5 text-sm"
              >
                Delete Role
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Roles;